import React, { useState, useEffect } from "react";
// New code: added Select to the imports
import { Row, Col, Card, Button, Modal, Input, message, Select } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import "antd/dist/antd.css";
import ResponsiveTable from "../components/ResponsiveTable";
import LoadingSpinner from "../components/LoadingSpinner"; // Added LoadingSpinner import

// New code: extracted Option from Select
const { Option } = Select;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SystemUserManagement = () => {
  const [data, setData] = useState([]);
  // New code: added projects state
  const [projects, setProjects] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Added loading state
  const [newRecord, setNewRecord] = useState({
    // userid: "", // Remove userid from add form state
    name: "",
    project_id: "",
  });
  const [searchText, setSearchText] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  // Fetch system users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // Use the production API endpoint for fetching system users
        const response = await fetch(`${API_BASE_URL}/system_users`);
        if (!response.ok) throw new Error("Failed to fetch system users");
        const users = await response.json();
        setData(
          users.map((user) => ({
            key: user.userid,
            userid: user.userid,
            name: user.name,
            project_id: user.project_id,
          }))
        );
      } catch (error) {
        message.error("Error fetching system users");
      } finally {
        setIsInitialLoad(false); // Turn off loading spinner
      }
    };

    fetchUsers();
    const interval = setInterval(fetchUsers, 30000);
    return () => clearInterval(interval);
  }, []);

  // New code: added useEffect to fetch projects for the dropdown
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/projects/`);
        if (response.ok) {
          const result = await response.json();
          setProjects(result);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    setFilteredData(
      data.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchText.toLowerCase())
        )
      )
    );
  }, [searchText, data]);

  // Add system user API
  const addNewRecord = async () => {
    try {
      // Only send name and project_id as per your API
      const response = await fetch(`${API_BASE_URL}/system_users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: newRecord.name,
          project_id: newRecord.project_id,
        }),
      });
      if (!response.ok) throw new Error("Failed to add system user");
      const user = await response.json();
      setData((prevData) => [
        ...prevData,
        {
          key: user.userid,
          userid: user.userid,
          name: user.name,
          project_id: user.project_id,
        },
      ]);
      setIsAdding(false);
      setNewRecord({
        name: "",
        project_id: "",
      });
      // Optionally show a success message
    } catch (error) {
      // Optionally show an error message
    }
  };

  // Update system user API
  const saveEdit = async () => {
    try {
      // Use the correct PUT API endpoint and body for editing system user
      const response = await fetch(
        `${API_BASE_URL}/system_users/${editingRecord.userid}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: editingRecord.name,
            project_id: editingRecord.project_id,
          }),
        }
      );
      if (!response.ok) throw new Error("Failed to update system user");
      const user = await response.json();
      setData((prevData) =>
        prevData.map((item) =>
          item.userid === editingRecord.userid
            ? {
              key: user.userid,
              userid: user.userid,
              name: user.name,
              project_id: user.project_id,
            }
            : item
        )
      );
      setIsEditing(false);
      setEditingRecord(null);
      // Optionally show a success message
    } catch (error) {
      // Optionally show an error message
    }
  };

  // Delete system user API
  const deleteRow = async (key) => {
    try {
      // Use the correct DELETE API endpoint for deleting a system user
      const response = await fetch(
        `${API_BASE_URL}/system_users/${key}`,
        { method: "DELETE" }
      );
      if (!response.ok) throw new Error("Failed to delete system user");
      // Optionally, you can check the response message here
      setData((prevData) => prevData.filter((item) => item.key !== key));
      // Optionally show a success message
    } catch (error) {
      // Optionally show an error message
    }
  };

  const editRow = (record) => {
    setIsEditing(true);
    setEditingRecord({ ...record });
  };

  const columns = [
    {
      title: "USER ID",
      dataIndex: "userid",
      key: "userid",
      sorter: (a, b) => a.userid.localeCompare(b.userid),
    },
    {
      title: "NAME",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "PROJECT ID",
      dataIndex: "project_id",
      key: "project_id",
      sorter: (a, b) => a.project_id.localeCompare(b.project_id),
    },
    {
      title: "ACTION",
      key: "action",
      render: (_, record) => (
        <>
          <Button
            icon={<EditOutlined style={{ color: "#1890ff" }} />}
            onClick={() => editRow(record)}
            style={{ marginRight: 8, color: "#1890ff", borderColor: "#1890ff" }}
          >
            Edit
          </Button>
          <Button onClick={() => deleteRow(record.userid)} danger>
            ❌ Delete
          </Button>
        </>
      ),
    },
  ];

  return (
    <div className="system-user-management">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            bordered={false}
            className="criclebox tablespace mb-24"
            title="System User Management Table"
            extra={
              <>
                <Input
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 200, marginRight: 8 }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAdding(true)}
                >
                  Add System User
                </Button>
              </>
            }
          >
            {isInitialLoad ? (
              <LoadingSpinner text="Loading records..." />
            ) : (
              <div className="table-responsive">
                <ResponsiveTable
                  columns={columns}
                  dataSource={filteredData}
                  pagination={{ pageSize: 10 }}
                  scroll={{ x: 900 }}
                  className="ant-border-space"
                />
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Edit Modal */}
      <Modal
        title="Edit System User"
        visible={isEditing}
        onOk={saveEdit}
        onCancel={() => {
          setIsEditing(false);
          setEditingRecord(null);
        }}
      >
        {/* Remove User ID input from Edit Modal */}
        <Input
          placeholder="Name"
          value={editingRecord?.name}
          onChange={(e) =>
            setEditingRecord({ ...editingRecord, name: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />

        {/* Old code commented out:
        <Input
          placeholder="Project ID"
          value={editingRecord?.project_id}
          onChange={(e) =>
            setEditingRecord({ ...editingRecord, project_id: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        */}

        {/* New code: Added Select Dropdown for Project ID */}
        <Select
          placeholder="Select Project"
          value={editingRecord?.project_id || undefined}
          style={{ width: '100%', marginBottom: 8 }}
          onChange={(value) => setEditingRecord({ ...editingRecord, project_id: value })}
        >
          {projects.map((proj) => (
            <Option key={proj.project_id} value={proj.project_id}>
              {proj.project_name} ({proj.project_id})
            </Option>
          ))}
        </Select>

      </Modal>

      {/* Add Modal */}
      <Modal
        title="Add New System User"
        visible={isAdding}
        onOk={addNewRecord}
        onCancel={() => setIsAdding(false)}
      >
        {/* Remove User ID input from Add Modal */}
        <Input
          placeholder="Name"
          value={newRecord.name}
          onChange={(e) =>
            setNewRecord({ ...newRecord, name: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />

        {/* Old code commented out:
        <Input
          placeholder="Project ID"
          value={newRecord.project_id}
          onChange={(e) =>
            setNewRecord({ ...newRecord, project_id: e.target.value })
          }
          style={{ marginBottom: 8 }}
        />
        */}

        {/* New code: Added Select Dropdown for Project ID */}
        <Select
          placeholder="Select Project"
          value={newRecord.project_id || undefined}
          style={{ width: '100%', marginBottom: 8 }}
          onChange={(value) => setNewRecord({ ...newRecord, project_id: value })}
        >
          {projects.map((proj) => (
            <Option key={proj.project_id} value={proj.project_id}>
              {proj.project_name} ({proj.project_id})
            </Option>
          ))}
        </Select>

      </Modal>
    </div>
  );
};

export default SystemUserManagement;